import React from "react";
import { Modal } from "antd";
import { useTranslation } from "react-i18next";

import { TimedActionDraft, ControlOption, ScheduledActionSelectors } from "./scheduledActionTypes";
import ScheduledActionForm from "./ScheduledActionForm";

// ── Props ────────────────────────────────────────────────────────────────────

export interface TimedActionEditModalProps {
  /** The draft being created/edited, or null when the modal is idle. The form
   *  mounts only while this is non-null; a falsy `id` means "create". */
  editDraft: TimedActionDraft | null;
  open: boolean;
  controlOptions: ControlOption[];
  timezoneDefault: string;
  submitting: boolean;
  onSubmit: (draft: TimedActionDraft) => void;
  onCancel: () => void;
  /** Bank + lift pickers for retargeting an action. Supplied by the wizard;
   *  omitted by the control-panel containers, which scope to a fixed bank/lift. */
  selectors?: ScheduledActionSelectors;
}

// ── Component ────────────────────────────────────────────────────────────────

/**
 * The create/edit modal shell shared by every create-capable scheduled-action
 * container (wizard, control-panel bank, control-panel lift). Owns the antd
 * `Modal` chrome, the New/Edit title, and the `editDraft &&` mount guard so the
 * containers keep only their own selectors and layout. `destroyOnClose` resets
 * the inner form between opens.
 */
function TimedActionEditModal({
  editDraft,
  open,
  controlOptions,
  timezoneDefault,
  submitting,
  onSubmit,
  onCancel,
  selectors = undefined,
}: TimedActionEditModalProps): React.ReactElement {
  const [t] = useTranslation("global");

  return (
    <Modal
      title={
        editDraft?.id
          ? t("scheduledActions.editAction", "Edit Scheduled Action")
          : t("scheduledActions.newAction", "New Scheduled Action")
      }
      open={open}
      onCancel={onCancel}
      footer={null}
      destroyOnClose
    >
      {editDraft && (
        <ScheduledActionForm
          initial={editDraft}
          controlOptions={controlOptions}
          selectors={selectors}
          timezoneDefault={timezoneDefault}
          submitting={submitting}
          onSubmit={onSubmit}
          onCancel={onCancel}
        />
      )}
    </Modal>
  );
}

export default TimedActionEditModal;
